import React, { createContext, useState, useContext, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { CareerCenterLocationModel } from '../models/CareerCenterLocationModel';
import { StateModel } from '../models/StateModel';
import { fetchStates } from '../api/GoodWillApi';

interface StateContextType {
    selectedState: StateModel | null;
    setSelectedState: (state: StateModel | null) => void;
    availableStates: StateModel[];
    setLocations: (locations: CareerCenterLocationModel[]) => void;
    locations: CareerCenterLocationModel[];
    isStateLoaded: boolean;
}

const StateContext = createContext<StateContextType | undefined>(undefined);

export const StateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [selectedState, setSelectedState] = useState<StateModel | null>(null);
    const [locations, setLocationsState] = useState<CareerCenterLocationModel[]>([]);
    const [availableStates, setAvailableStates] = useState<StateModel[]>([]);
    const [isStateLoaded, setIsStateLoaded] = useState(false);

    const detectLocation = useCallback(async (states: StateModel[]) => {
        if (!navigator.geolocation) return null;

        return new Promise<StateModel | null>((resolve) => {
            const timeoutId = setTimeout(() => {
                console.warn("Location detection timed out.");
                resolve(null);
            }, 5000);

            navigator.geolocation.getCurrentPosition(async (position) => {
                clearTimeout(timeoutId);
                const userLat = position.coords.latitude;
                const userLon = position.coords.longitude;

                try {
                    const response = await fetch(
                        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${userLat}&longitude=${userLon}&localityLanguage=en`
                    );
                    const data = await response.json();
                    const detectedStateName = data.principalSubdivision;

                    if (detectedStateName) {
                        const matchedState = states.find(s => 
                            s.name.toLowerCase() === detectedStateName.toLowerCase() ||
                            detectedStateName.toLowerCase().includes(s.name.toLowerCase()) ||
                            s.name.toLowerCase().includes(detectedStateName.toLowerCase())
                        );
                        resolve(matchedState || null);
                    } else {
                        resolve(null);
                    }
                } catch (error) {
                    console.error("Error performing reverse geocoding", error);
                    resolve(null);
                }
            }, (error) => {
                clearTimeout(timeoutId);
                console.warn("Geolocation permission denied or error", error);
                resolve(null);
            });
        });
    }, []);

    // Load states from API
    useEffect(() => {
        const loadStates = async () => {
            try {
                const data = await fetchStates();

                if (Array.isArray(data) && data.length > 0) {
                    setAvailableStates(data);
                    
                    const savedStateId = localStorage.getItem('selectedStateId');
                    
                    if (savedStateId) {
                        const savedState = data.find(s => s.id.toString() === savedStateId);
                        if (savedState) {
                            setSelectedState(savedState);
                            setIsStateLoaded(true);
                            return;
                        }
                    }

                    const detectedState = await detectLocation(data);
                    
                    if (detectedState) {
                        setSelectedState(detectedState);
                    } else {
                        setSelectedState(data[0]);
                    }
                    setIsStateLoaded(true);
                } else {
                    console.warn("StateProvider: fetchStates returned no web-enabled states. Check 'AssignToWeb' column in DB.");
                    setIsStateLoaded(true);
                }
            } catch (error) {
                console.error("StateProvider: Failed to load states:", error);
                setIsStateLoaded(true);
            }
        };
        loadStates();
    }, [detectLocation]);

    // Save to local storage when changed
    useEffect(() => {
        if (selectedState) {
            localStorage.setItem('selectedStateId', selectedState.id.toString());
        }
    }, [selectedState]);

    const setLocations = useCallback((newLocations: CareerCenterLocationModel[]) => {
        setLocationsState(newLocations);
    }, []);

    const value = useMemo(() => ({
        selectedState,
        setSelectedState,
        availableStates,
        setLocations,
        locations,
        isStateLoaded
    }), [selectedState, availableStates, setLocations, locations, isStateLoaded]);

    return (
        <StateContext.Provider value={value}>
            {children}
        </StateContext.Provider>
    );
};

export const useStateContext = () => {
    const context = useContext(StateContext);
    if (context === undefined) {
        throw new Error('useStateContext must be used within a StateProvider');
    }
    return context;
};